import { useEffect, useMemo, useState } from 'react'
import {
	Autocomplete,
	Box,
	Chip,
	MenuItem,
	Stack,
	TextField,
	Typography,
} from '@mui/material'
import ButtonCustom from '../atoms/button/ButtonCustom'
import CommonModal from '../atoms/modal/CommonModal'
import InputCustom from '../atoms/input/InputCustom'
import styles from '../popups/UpdateKazooGuestPopup.module.scss'
import { filterSubstringOptions, normalizeTagList } from '../../util/tagNormalize'

const PARTICIPATION_STATUSES = [
	'Draft',
	'Invited',
	'Accepted',
	'Declined',
	'Cancelled',
]

const DIETARY_OPTIONS = [
	'Vegetarian',
	'Vegan',
	'Gluten Free',
	'Nut Allergy',
	'Dairy Free',
	'Kosher',
	'Halal',
]

const chipSx = {
	'&.MuiChip-root': {
		bgcolor: '#F2F6F8',
		borderRadius: '4px',
	},
	'& .MuiChip-label': {
		fontSize: '12px',
		fontFamily: 'Inter',
		fontWeight: 500,
		color: '#313131',
	},
}

const emptyForm = {
	firstName: '',
	lastName: '',
	preferredName: '',
	pronouns: '',
	email: '',
	phone: '',
	roleInWedding: 'Guest',
	guestTags: [],
	notes: '',
	additionalGuestAllowance: 0,
	dietaryNotes: '',
	dietaryRestrictions: [],
	eventTag: '',
	eventStatus: 'Draft',
	eventParticipation: [],
	customFieldValues: {},
}

function guestToForm(guest) {
	if (!guest) return { ...emptyForm, guestTags: [], eventParticipation: [], dietaryRestrictions: [], customFieldValues: {} }
	const customFieldValues = {}
	for (const entry of guest.customFieldValues || []) {
		customFieldValues[entry.fieldId] = entry.value
	}
	return {
		firstName: guest.firstName || '',
		lastName: guest.lastName || '',
		preferredName: guest.preferredName || '',
		pronouns: guest.pronouns || '',
		email: guest.email || '',
		phone: guest.phone || '',
		roleInWedding: guest.roleInWedding || 'Guest',
		guestTags: [...(guest.guestTags || [])],
		notes: guest.notes || '',
		additionalGuestAllowance: guest.additionalGuestAllowance ?? 0,
		dietaryNotes: guest.dietary?.notes || '',
		dietaryRestrictions: guest.dietary?.restrictions || [],
		eventTag: '',
		eventStatus: 'Draft',
		eventParticipation: guest.eventParticipation || [],
		customFieldValues,
	}
}

function buildPayload(form, definitions, { isCreate, forceCreate }) {
	const customFieldValues = (definitions || [])
		.map((def) => {
			const value = form.customFieldValues?.[def._id]
			if (value === undefined || value === '' || value === null) return null
			return { fieldId: def._id, value }
		})
		.filter(Boolean)

	const payload = {
		firstName: form.firstName.trim(),
		lastName: form.lastName.trim(),
		preferredName: form.preferredName.trim() || undefined,
		pronouns: form.pronouns.trim() || undefined,
		email: form.email.trim() || undefined,
		phone: form.phone.trim() || undefined,
		roleInWedding: form.roleInWedding.trim() || 'Guest',
		guestTags: form.guestTags || [],
		notes: form.notes,
		additionalGuestAllowance: Number(form.additionalGuestAllowance) || 0,
		dietary: {
			restrictions: form.dietaryRestrictions,
			notes: form.dietaryNotes || '',
		},
		eventParticipation: form.eventParticipation,
		customFieldValues,
	}

	if (isCreate && forceCreate) {
		payload.duplicateResolution = 'create'
	}

	return payload
}

export default function GuestFormModal({
	open,
	onClose,
	guest = null,
	guestTags = [],
	eventTags = [],
	customFieldDefinitions = [],
	onSubmit,
}) {
	const isEdit = Boolean(guest?._id)
	const [form, setForm] = useState(emptyForm)
	const [submitting, setSubmitting] = useState(false)
	const [forceCreate, setForceCreate] = useState(false)

	useEffect(() => {
		if (open) {
			setForm(guestToForm(guest))
			setForceCreate(false)
			setSubmitting(false)
		}
	}, [open, guest])

	const guestTagCatalog = useMemo(() => {
		return Array.from(new Set([...(guestTags || [])].filter(Boolean))).sort()
	}, [guestTags])

	const eventTagCatalog = useMemo(() => {
		return Array.from(new Set([...(eventTags || [])].filter(Boolean))).sort()
	}, [eventTags])

	/** Suggestions exclude tags already on this guest. */
	const guestTagSuggestions = useMemo(() => {
		const assigned = new Set(
			(form.guestTags || []).map((t) => t.toLowerCase())
		)
		return guestTagCatalog.filter((t) => !assigned.has(t.toLowerCase()))
	}, [guestTagCatalog, form.guestTags])

	/** Event tag suggestions exclude tags already in participation. */
	const eventTagSuggestions = useMemo(() => {
		const assigned = new Set(
			(form.eventParticipation || []).map((p) => p.eventTag.toLowerCase())
		)
		return eventTagCatalog.filter((t) => !assigned.has(t.toLowerCase()))
	}, [eventTagCatalog, form.eventParticipation])

	const setField = (name, value) => {
		setForm((prev) => ({ ...prev, [name]: value }))
	}

	const toggleDietary = (restriction) => {
		setForm((prev) => {
			const has = prev.dietaryRestrictions.includes(restriction)
			return {
				...prev,
				dietaryRestrictions: has
					? prev.dietaryRestrictions.filter((r) => r !== restriction)
					: [...prev.dietaryRestrictions, restriction],
			}
		})
	}

	const addParticipation = () => {
		const tag = form.eventTag.trim()
		if (!tag) return
		const canonical =
			eventTagCatalog.find((t) => t.toLowerCase() === tag.toLowerCase()) || tag
		setForm((prev) => {
			const without = prev.eventParticipation.filter(
				(p) => p.eventTag.toLowerCase() !== canonical.toLowerCase()
			)
			return {
				...prev,
				eventParticipation: [
					...without,
					{ eventTag: canonical, status: prev.eventStatus || 'Draft' },
				],
				eventTag: '',
			}
		})
	}

	const removeParticipation = (eventTag) => {
		setForm((prev) => ({
			...prev,
			eventParticipation: prev.eventParticipation.filter((p) => p.eventTag !== eventTag),
		}))
	}

	const updateParticipationStatus = (eventTag, status) => {
		setForm((prev) => ({
			...prev,
			eventParticipation: prev.eventParticipation.map((p) =>
				p.eventTag === eventTag ? { ...p, status } : p
			),
		}))
	}

	const handleGuestTagsChange = (_event, newValue) => {
		setField('guestTags', normalizeTagList(newValue, guestTagCatalog))
	}

	const handleSubmit = async (e) => {
		e.preventDefault()
		if (submitting) return
		if (!form.firstName.trim() || !form.lastName.trim()) return

		setSubmitting(true)
		try {
			const payload = buildPayload(form, customFieldDefinitions, {
				isCreate: !isEdit,
				forceCreate,
			})
			await onSubmit?.(payload)
		} finally {
			setSubmitting(false)
		}
	}

	return (
		<CommonModal
			open={open}
			handleClose={onClose}
			heading={isEdit ? 'Edit Guest' : 'Add Guest'}
			subHeading="Registry details for this wedding"
			data-cy="guest-form-modal"
			footerContent={
				<>
					<ButtonCustom variant="outlined" size="lg" onClick={onClose}>
						Cancel
					</ButtonCustom>
					<ButtonCustom
						variant="default"
						size="lg"
						onClick={handleSubmit}
						disabled={submitting}
						data-cy="guest-form-submit"
					>
						{isEdit ? 'Save Changes' : 'Add Guest'}
					</ButtonCustom>
				</>
			}
		>
			<form onSubmit={handleSubmit} className={styles.formWrp}>
				<Stack spacing={2}>
					<Box className={styles.formField}>
						<span className={styles.label}>First name *</span>
						<InputCustom
							name="firstName"
							placeholder="First name"
							value={form.firstName}
							data={form}
							setDataFunction={setForm}
							required
							data_cy="guest-first-name"
						/>
					</Box>
					<Box className={styles.formField}>
						<span className={styles.label}>Last name *</span>
						<InputCustom
							name="lastName"
							placeholder="Last name"
							value={form.lastName}
							data={form}
							setDataFunction={setForm}
							required
							data_cy="guest-last-name"
						/>
					</Box>
					<Box className={styles.formField}>
						<span className={styles.label}>Preferred name</span>
						<InputCustom
							name="preferredName"
							placeholder="Optional"
							value={form.preferredName}
							data={form}
							setDataFunction={setForm}
						/>
					</Box>
					<Box className={styles.formField}>
						<span className={styles.label}>Pronouns</span>
						<InputCustom
							name="pronouns"
							placeholder="Optional"
							value={form.pronouns}
							data={form}
							setDataFunction={setForm}
						/>
					</Box>
					<Box className={styles.formField}>
						<span className={styles.label}>Email</span>
						<InputCustom
							name="email"
							type="email"
							placeholder="name@example.com"
							value={form.email}
							data={form}
							setDataFunction={setForm}
							data_cy="guest-email"
						/>
					</Box>
					<Box className={styles.formField}>
						<span className={styles.label}>Phone</span>
						<InputCustom
							name="phone"
							placeholder="Optional"
							value={form.phone}
							data={form}
							setDataFunction={setForm}
						/>
					</Box>
					<Box className={styles.formField}>
						<span className={styles.label}>Role in wedding</span>
						<InputCustom
							name="roleInWedding"
							placeholder="Guest"
							value={form.roleInWedding}
							data={form}
							setDataFunction={setForm}
							data_cy="guest-role"
						/>
					</Box>
					<Box className={styles.formField}>
						<span className={styles.label}>Guest tags</span>
						<Autocomplete
							multiple
							freeSolo
							openOnFocus
							options={guestTagSuggestions}
							filterOptions={filterSubstringOptions}
							value={form.guestTags}
							onChange={handleGuestTagsChange}
							renderTags={(value, getTagProps) =>
								value.map((option, index) => {
									const { key, ...tagProps } = getTagProps({ index })
									return (
										<Chip
											key={key}
											label={option}
											size="small"
											sx={chipSx}
											{...tagProps}
										/>
									)
								})
							}
							renderInput={(params) => (
								<TextField
									{...params}
									size="small"
									placeholder={
										form.guestTags.length
											? 'Add another tag'
											: 'Type or select a tag'
									}
									inputProps={{
										...params.inputProps,
										'data-cy': 'guest-tags',
									}}
								/>
							)}
						/>
					</Box>

					<Box className={styles.formField}>
						<span className={styles.label}>Event participation</span>
						<Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
							<Autocomplete
								freeSolo
								openOnFocus
								options={eventTagSuggestions}
								filterOptions={filterSubstringOptions}
								value={form.eventTag}
								onChange={(_e, newValue) =>
									setField(
										'eventTag',
										typeof newValue === 'string' ? newValue : newValue || ''
									)
								}
								onInputChange={(_e, newInput, reason) => {
									if (reason === 'input' || reason === 'clear') {
										setField('eventTag', newInput)
									}
								}}
								sx={{ flex: 1 }}
								renderInput={(params) => (
									<TextField
										{...params}
										size="small"
										placeholder="Event tag"
										inputProps={{
											...params.inputProps,
											'data-cy': 'guest-event-tag',
										}}
									/>
								)}
							/>
							<TextField
								select
								size="small"
								value={form.eventStatus}
								onChange={(e) => setField('eventStatus', e.target.value)}
								sx={{ minWidth: 140 }}
								inputProps={{ 'data-cy': 'guest-event-status' }}
							>
								{PARTICIPATION_STATUSES.map((status) => (
									<MenuItem key={status} value={status}>
										{status}
									</MenuItem>
								))}
							</TextField>
							<ButtonCustom
								variant="outlined"
								onClick={addParticipation}
								type="button"
								data-cy="guest-event-add"
							>
								Add
							</ButtonCustom>
						</Stack>
						<Stack spacing={1} sx={{ mt: 0.5 }} data-cy="guest-participation-list">
							{form.eventParticipation.map((p) => (
								<Stack
									key={p.eventTag}
									direction="row"
									spacing={1}
									alignItems="center"
									flexWrap="wrap"
									useFlexGap
								>
									<Chip label={p.eventTag} size="small" sx={chipSx} />
									<TextField
										select
										size="small"
										value={p.status}
										onChange={(e) =>
											updateParticipationStatus(p.eventTag, e.target.value)
										}
										sx={{ minWidth: 140 }}
										inputProps={{
											'data-cy': `guest-participation-status-${p.eventTag}`,
										}}
									>
										{PARTICIPATION_STATUSES.map((status) => (
											<MenuItem key={status} value={status}>
												{status}
											</MenuItem>
										))}
									</TextField>
									<ButtonCustom
										variant="outlined"
										type="button"
										onClick={() => removeParticipation(p.eventTag)}
										data-cy={`guest-participation-remove-${p.eventTag}`}
									>
										Remove
									</ButtonCustom>
								</Stack>
							))}
						</Stack>
					</Box>

					<Box className={styles.formField}>
						<span className={styles.label}>Dietary restrictions</span>
						<Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
							{DIETARY_OPTIONS.map((opt) => {
								const selected = form.dietaryRestrictions.includes(opt)
								return (
									<Chip
										key={opt}
										label={opt}
										clickable
										variant={selected ? 'filled' : 'outlined'}
										onClick={() => toggleDietary(opt)}
										size="small"
										sx={
											selected
												? {
														backgroundColor: '#7CA6BD',
														color: '#fff',
														'&:hover': { backgroundColor: '#6a94ab' },
													}
												: undefined
										}
									/>
								)
							})}
						</Stack>
						<InputCustom
							name="dietaryNotes"
							placeholder="Dietary notes"
							value={form.dietaryNotes}
							data={form}
							setDataFunction={setForm}
						/>
					</Box>

					<Box className={styles.formField}>
						<span className={styles.label}>Additional guest allowance</span>
						<InputCustom
							name="additionalGuestAllowance"
							type="number"
							min={0}
							value={form.additionalGuestAllowance}
							data={form}
							setDataFunction={setForm}
						/>
					</Box>

					{(customFieldDefinitions || []).length > 0 && (
						<Box>
							<Typography sx={{ fontWeight: 600, mb: 1 }}>Custom fields</Typography>
							<Stack spacing={1.5}>
								{customFieldDefinitions.map((def) => (
									<Box key={def._id} className={styles.formField}>
										<span className={styles.label}>
											{def.name}
											{def.required ? ' *' : ''}
										</span>
										{def.type === 'Boolean' ? (
											<TextField
												select
												size="small"
												fullWidth
												value={
													form.customFieldValues[def._id] === true
														? 'true'
														: form.customFieldValues[def._id] === false
															? 'false'
															: ''
												}
												onChange={(e) =>
													setForm((prev) => ({
														...prev,
														customFieldValues: {
															...prev.customFieldValues,
															[def._id]:
																e.target.value === ''
																	? undefined
																	: e.target.value === 'true',
														},
													}))
												}
											>
												<MenuItem value="">—</MenuItem>
												<MenuItem value="true">Yes</MenuItem>
												<MenuItem value="false">No</MenuItem>
											</TextField>
										) : def.type === 'Dropdown' ? (
											<TextField
												select
												size="small"
												fullWidth
												value={form.customFieldValues[def._id] ?? ''}
												onChange={(e) =>
													setForm((prev) => ({
														...prev,
														customFieldValues: {
															...prev.customFieldValues,
															[def._id]: e.target.value,
														},
													}))
												}
											>
												<MenuItem value="">—</MenuItem>
												{(def.options || []).map((opt) => (
													<MenuItem key={opt} value={opt}>
														{opt}
													</MenuItem>
												))}
											</TextField>
										) : (
											<TextField
												size="small"
												fullWidth
												type={
													def.type === 'Number'
														? 'number'
														: def.type === 'Date'
															? 'date'
															: 'text'
												}
												value={form.customFieldValues[def._id] ?? ''}
												onChange={(e) =>
													setForm((prev) => ({
														...prev,
														customFieldValues: {
															...prev.customFieldValues,
															[def._id]:
																def.type === 'Number'
																	? Number(e.target.value)
																	: e.target.value,
														},
													}))
												}
											/>
										)}
									</Box>
								))}
							</Stack>
						</Box>
					)}

					<Box className={styles.formField}>
						<span className={styles.label}>Notes</span>
						<TextField
							multiline
							minRows={2}
							fullWidth
							value={form.notes}
							onChange={(e) => setField('notes', e.target.value)}
							placeholder="Planner notes"
							inputProps={{ 'data-cy': 'guest-notes' }}
						/>
					</Box>

					{!isEdit && (
						<Typography variant="body2" color="text.secondary">
							If a duplicate is detected, submit again with “Create anyway” or edit the
							existing guest.
							{forceCreate ? ' Create-anyway is enabled for this submit.' : ''}
						</Typography>
					)}
					{!isEdit && (
						<ButtonCustom
							variant="outlined"
							type="button"
							onClick={() => setForceCreate(true)}
							data-cy="guest-force-create"
						>
							Allow create if duplicate
						</ButtonCustom>
					)}
				</Stack>
			</form>
		</CommonModal>
	)
}
